import React from 'react';
import RelatedTools from './RelatedTools';

interface SEOContentProps {
  title: string;
  description: string;
  features: string[];
  benefits: string[];
  howItWorks: { step: number; title: string; description: string }[];
  faq?: { question: string; answer: string }[];
  currentTool?: string;
}

const SEOContent: React.FC<SEOContentProps> = ({
  title,
  description,
  features,
  benefits,
  howItWorks,
  faq,
  currentTool
}) => {
  return (
    <div className="mt-16 bg-bg-secondary rounded-xl p-8 border border-border-primary">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl font-bold text-text-primary mb-4">{title}</h2>
        <p className="text-lg text-text-secondary mb-8">{description}</p>

        <section className="mb-10">
          <h3 className="text-2xl font-semibold text-text-primary mb-4">Key Features</h3>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <svg
                  className="h-6 w-6 text-success mr-2 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-text-secondary">{feature}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-10">
          <h3 className="text-2xl font-semibold text-text-primary mb-4">Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-bg-primary p-4 rounded-lg border border-border-primary">
                <p className="text-text-secondary">{benefit}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h3 className="text-2xl font-semibold text-text-primary mb-4">How It Works</h3>
          <div className="space-y-4">
            {howItWorks.map((step) => (
              <div key={step.step} className="flex">
                <div className="flex-shrink-0 w-10 h-10 bg-brand-primary text-text-button rounded-full flex items-center justify-center font-bold">
                  {step.step}
                </div>
                <div className="ml-4">
                  <h4 className="font-semibold text-text-primary">{step.title}</h4>
                  <p className="text-text-secondary">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {faq && faq.length > 0 && (
          <section>
            <h3 className="text-2xl font-semibold text-text-primary mb-4">Frequently Asked Questions</h3>
            <div className="space-y-4">
              {faq.map((item, index) => (
                <div key={index} className="bg-bg-primary p-4 rounded-lg border border-border-primary">
                  <h4 className="font-semibold text-text-primary mb-2">{item.question}</h4>
                  <p className="text-text-secondary">{item.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {currentTool && <RelatedTools currentTool={currentTool} />}
      </div>
    </div>
  );
};

export default SEOContent;